import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// "09:00" -> "9am", "18:30" -> "6:30pm", "20:00" -> "8pm"
export function formatTime12(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  let hr = h % 12;
  if (hr === 0) hr = 12;
  return m === 0 ? `${hr}${ampm}` : `${hr}:${String(m).padStart(2, '0')}${ampm}`;
}

// Group consecutive open days into ranges and append the single daily window.
// e.g. { open_days:[1,2,3,4,5], open_time:'09:00', close_time:'18:00' }
//   -> "Mon–Fri 9am–6pm"
export function formatHours(schedule) {
  if (!schedule) return '';
  const open = (schedule.open_days || []).slice().sort((a, b) => a - b);
  if (open.length === 0) return 'Closed';
  const ranges = [];
  let start = open[0];
  let prev = open[0];
  for (let i = 1; i < open.length; i++) {
    if (open[i] === prev + 1) {
      prev = open[i];
    } else {
      ranges.push([start, prev]);
      start = open[i];
      prev = open[i];
    }
  }
  ranges.push([start, prev]);
  const dayStr = ranges
    .map(([a, b]) => (a === b ? DAY_NAMES[a] : `${DAY_NAMES[a]}–${DAY_NAMES[b]}`))
    .join(', ');
  return `${dayStr} ${formatTime12(schedule.open_time)}–${formatTime12(schedule.close_time)}`;
}

// True if the venue is open right now according to this schedule (browser local time).
export function isOpenNow(schedule) {
  if (!schedule) return false;
  const days = schedule.open_days || [];
  if (days.length === 0) return false;
  const now = new Date();
  if (!days.includes(now.getDay())) return false;
  const [oh, om] = (schedule.open_time || '').split(':').map(Number);
  const [ch, cm] = (schedule.close_time || '').split(':').map(Number);
  const cur = now.getHours() * 60 + now.getMinutes();
  const o = oh * 60 + om;
  const c = ch * 60 + cm;
  return cur >= o && cur <= c;
}

// Fetches both opening-hours schedules (storefront + experience) and exposes
// helpers to format / evaluate them. Public read, so works without auth.
export function useOpeningHours() {
  const { data = [], isLoading } = useQuery({
    queryKey: ['opening-hours'],
    queryFn: () => base44.entities.OpeningHours.list(),
    staleTime: 1000 * 60 * 2,
  });

  const byKey = {};
  for (const r of data) {
    if (r.schedule_key) byKey[r.schedule_key] = r;
  }

  return {
    storefront: byKey.storefront,
    experience: byKey.experience,
    byKey,
    isLoading,
    formatHours,
    formatTime12,
    isOpenNow,
  };
}